import useClientStore from "@/lib/clientStore"
import { Fragment, useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"
import { useOutsideClick, useScreen } from "@/hooks"
import IconButton from "../IconButton"
import classNames from "classnames"
import { ArrowSquareLeftIcon, ClockIcon, SearchIcon, XIcon } from "../shared/icons"
import { googleAnalytics } from "@/lib/gtag"
import { useAuth } from "@/providers/authContext"
import { ChatHistory } from "./ChatHistory"
import Input from "../Input"
import { NewConversation } from "../shared/icons/NewConversation"
import { useRouter } from "next/router"
import { useChatStore } from "@/stores/useChatStore"
import useInfiniteConversationsQuery from "@/queries/ai-agent/useInfiniteConversationsQuery"
import Tooltip from "@/components/Tooltip"
import { useCreateConversation } from "@/queries/ai-agent/useConversation"
import { ConversationDto } from "@/api/sdk"

const ChatSidebar = () => {
  const router = useRouter()
  const { toggleSidebar, isOpenSidebar } = useClientStore()
  const { width } = useScreen()
  const { user } = useAuth()
  const { searchConversation, setSearchConversation, clearSearchConversation, setInitialPrompt } = useChatStore()
  const { refetch: refetchConversations } = useInfiniteConversationsQuery()
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick({
    ref,
    enabled: isOpenSidebar,
    handler: () => toggleSidebar(),
  })

  // Add debounced search effect
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set a new timeout to trigger search after 1000ms
    if (searchConversation !== undefined) {
      searchTimeoutRef.current = setTimeout(() => {
        setIsSearching(true)
        refetchConversations().finally(() => {
          setIsSearching(false)
        })
      }, 1000)
    }

    // Cleanup function to clear timeout when component unmounts or searchConversation changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchConversation, refetchConversations])

  // Handle input change with reset functionality
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchConversation(newValue)

    // If input is cleared completely, immediately reset results
    if (newValue === "") {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      setIsSearching(true)
      refetchConversations().finally(() => {
        setIsSearching(false)
      })
    }
  }
  const { createConversation } = useCreateConversation()

  if (!user) return null

  const handleEventToggleSidebar = () => {
    googleAnalytics.event({
      action: "click",
      category: "sidebar",
      label: `toggle_sidebar`,
      value: 1,
    })
    toggleSidebar()
  }

  const handleNewChat = () => {
    createConversation(
      {
        title: "New Conversation",
      },
      {
        onSuccess: (conversation: ConversationDto) => {
          // Extract sessionId from the message
          const conversationId = conversation.sessionId
          if (conversationId) {
            refetchConversations()
            // Navigate to the chat room with the session ID and prompt
            router.push(`/ai-agent/chat/${conversationId}`)
          }
        },
        onError: (error: Error) => {
          // TODO: Handle the errors by the way current project is handling the errors
          console.error("Failed to send message:", error)
        },
      },
    )
  }

  return (
    <Fragment>
      <motion.div
        ref={ref}
        className={cn(
          "fixed top-2 md:top-[5.5rem] left-0 bg-black z-[24] h-[calc(var(--vh,1vh)*100-5.15rem)] overflow-hidden md:h-[calc((var(--vh,1vh)*100)-5.5rem)]",
          "transition-all duration-300 ease-in-out ",
          {
            "w-[5rem] p-0 h-[32px] md:h-[32px]": !isOpenSidebar,
            "w-full md:w-80": isOpenSidebar,
          },
        )}
      >
        <motion.div
          animate={{
            opacity: isOpenSidebar ? 1 : 0,
          }}
          transition={{
            duration: 0.15,
          }}
          className={cn("h-full overflow-hidden", {
            hidden: !isOpenSidebar,
          })}
        >
          {/* AI AGENT SIDEBAR BODY */}
          <div className="w-full overflow-x-hidden overflow-y-auto h-full">
            {/* Search Bar and New Chat Button */}
            {/* <div className="flex items-center gap-2 mt-4 mb-4">
              <Input
                value={searchConversation}
                onChange={handleSearchInputChange}
                placeholder="Search messages"
                className="bg-atherGray-900"
                containerClassName="flex-1"
                rightIcon={
                  searchConversation ? (
                    isSearching ? (
                      <div className="w-4 h-4 animate-spin">
                        <svg className="text-atherGray-300" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    ) : (
                      <XIcon
                        width={16}
                        height={16}
                        className="cursor-pointer text-atherGray-300 hover:text-atherGray-0"
                        onClick={() => {
                          clearSearchConversation()
                          setIsSearching(true)
                          refetchConversations().finally(() => {
                            setIsSearching(false)
                          })
                        }}
                      />
                    )
                  ) : (
                    <SearchIcon width={16} height={16} className="text-atherGray-300" />
                  )
                }
                // Keep the Enter key functionality as a fallback
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    setIsSearching(true)
                    refetchConversations().finally(() => {
                      setIsSearching(false)
                    })
                  }
                }}
              />
            </div> */}
            <ChatHistory className="py-2" setIsOpenSidebar={toggleSidebar} />
          </div>
        </motion.div>
        <div
          className={classNames(
            "absolute top-[0]  flex gap-1 z-[24]",
            "transition-all left-2 duration-300 ease-in-out",
            {
              hidden: isOpenSidebar,
            },
          )}
        >
          <IconButton colorScheme="secondary" className="p-2 rounded-full" onClick={handleEventToggleSidebar}>
            <Tooltip
              trigger={
                <div className="">
                  <ClockIcon className="size-4" />
                </div>
              }
            >
              History
            </Tooltip>
          </IconButton>
          <button
            onClick={handleNewChat}
            className="p-2 rounded-full bg-[#5E17EB] text-atherGray-0  transition-colors flex items-center justify-center whitespace-nowrap"
          >
            <NewConversation width={16} height={16} />
          </button>
        </div>
      </motion.div>
    </Fragment>
  )
}

export default ChatSidebar
